import React from 'react';
import { useLocation } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { useI18n } from '../i18n/I18nProvider';
import { isNativeApp } from '../utils/appLinks';

const PULL_THRESHOLD = 72;
const MAX_PULL = 120;
const FEEDBACK_RESET_MS = 1600;

const getScrollContainer = (): HTMLElement | null => {
  if (typeof document === 'undefined') return null;
  return (document.querySelector('[data-scroll-root="true"]') as HTMLElement | null)
    ?? (document.scrollingElement as HTMLElement | null);
};

export default function MobilePullToRefresh() {
  const { t } = useI18n();
  const location = useLocation();
  const { gameState, refreshSnapshot } = useGame();
  const [pullDistance, setPullDistance] = React.useState(0);
  const [refreshing, setRefreshing] = React.useState(false);
  const [feedback, setFeedback] = React.useState<'ok' | 'fail' | null>(null);
  const pullDistanceRef = React.useRef(0);
  const startYRef = React.useRef(0);
  const pullingRef = React.useRef(false);
  const rafRef = React.useRef<number | null>(null);
  const refreshingRef = React.useRef(false);

  const isActiveOnlineGame = location.pathname.startsWith('/online/game/');
  const isOnlineLobby = location.pathname === '/online' || location.pathname === '/online/';
  const canManualRefresh = (
    (isActiveOnlineGame || isOnlineLobby)
    && gameState.mode === 'ONLINE'
    && Boolean(gameState.id)
  );
  const enabled = canManualRefresh && isNativeApp();

  const syncPullDistance = React.useCallback((value: number) => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      pullDistanceRef.current = value;
      setPullDistance(value);
      return;
    }
    pullDistanceRef.current = value;
    if (rafRef.current !== null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      setPullDistance(pullDistanceRef.current);
    });
  }, []);

  const setRefreshingSafe = React.useCallback((value: boolean) => {
    refreshingRef.current = value;
    setRefreshing(value);
  }, []);

  const triggerRefresh = React.useCallback(async () => {
    if (!enabled || refreshingRef.current) return;
    setRefreshingSafe(true);
    setFeedback(null);
    const ok = await refreshSnapshot('pull');
    setRefreshingSafe(false);
    setFeedback(ok ? 'ok' : 'fail');
    window.setTimeout(() => setFeedback(null), FEEDBACK_RESET_MS);
  }, [enabled, refreshSnapshot, setRefreshingSafe]);

  React.useEffect(() => {
    if (!enabled) {
      syncPullDistance(0);
      setFeedback(null);
      setRefreshingSafe(false);
      return;
    }
    if (typeof window === 'undefined') return;

    const onStart = (event: TouchEvent) => {
      if (refreshingRef.current) return;
      if (event.touches.length !== 1) return;
      const container = getScrollContainer();
      if (container && container.scrollTop > 0) return;
      startYRef.current = event.touches[0].clientY;
      pullingRef.current = true;
      syncPullDistance(0);
    };

    const onMove = (event: TouchEvent) => {
      if (!pullingRef.current) return;
      const container = getScrollContainer();
      if (container && container.scrollTop > 0) {
        pullingRef.current = false;
        syncPullDistance(0);
        return;
      }
      const delta = event.touches[0].clientY - startYRef.current;
      if (delta <= 0) {
        syncPullDistance(0);
        return;
      }
      const damped = Math.min(delta * 0.6, MAX_PULL);
      syncPullDistance(damped);
    };

    const onEnd = () => {
      if (!pullingRef.current) return;
      pullingRef.current = false;
      const distance = pullDistanceRef.current;
      syncPullDistance(0);
      if (distance >= PULL_THRESHOLD) {
        void triggerRefresh();
      }
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [enabled, syncPullDistance, triggerRefresh, setRefreshingSafe]);

  const showIndicator = enabled && (pullDistance > 0 || refreshing || feedback !== null);
  if (!showIndicator) return null;

  const label = refreshing
    ? t('settings.refreshing')
    : feedback === 'ok'
      ? t('settings.refreshDone')
      : feedback === 'fail'
        ? t('settings.refreshFailed')
        : t('settings.refreshAction');

  const progress = Math.min(pullDistance / PULL_THRESHOLD, 1);

  return (
    <div
      className="pointer-events-none fixed left-0 right-0 z-50 flex justify-center"
      style={{ top: 'calc(var(--app-safe-area-top) + 0.5rem)' }}
    >
      <div
        className="rounded-full border border-brand-border bg-(--color-bg-alt) px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-brand-text/70 shadow-[0_6px_18px_rgba(0,0,0,0.35)]"
        style={{
          transform: `translateY(${pullDistance}px)`,
          opacity: showIndicator ? 1 : 0,
          transition: refreshing || feedback ? 'opacity 180ms ease' : undefined,
        }}
        role="status"
        aria-live="polite"
      >
        <span>{label}</span>
        {!refreshing && feedback === null && (
          <span className="ml-2 inline-block h-1 w-12 overflow-hidden rounded-full bg-(--color-bg) align-middle">
            <span
              className="block h-full bg-accent"
              style={{ width: `${progress * 100}%` }}
            />
          </span>
        )}
      </div>
    </div>
  );
}
